import cx from "clsx"
import { Space, Text, SkeletonWrapper } from "components/common"
import { CURRENT_TENANT } from "constants/tenant"
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { numberConvertor } from "utils"
import { getCompactCurrencyType, getCurrencySymbol } from "utils/currencyHelpers"
import styles from "./index.module.less"

function usePrice() {
  const compactCurrencyType = useMemo(() => getCompactCurrencyType(), [CURRENT_TENANT])

  const attachCurrencyToPrice = ({
    price,
    isCurrency,
    currencyClassName = "",
    priceClassName = "",
    loading = false,
    loadingProps = {},
  }) => {
    if (!isCurrency) return price

    const currency = getCurrencySymbol()

    return (
      <Text 
        className={cx(styles.priceClass, priceClassName)} 
        loading={loading} 
        loadingProps={loadingProps}
        style={{ display: 'inline' }}
      >
        <span className={cx(styles.currencySymbolClass, currencyClassName)}>
          {currency}
        </span>
        {price}
      </Text>
    )
  }

  const getCompactedPrice = ({
    price,
    attachCurrency = true,
    skipCurrencyUnit = false,
    digitAfterFraction = 2,
    priceClassName = "",
    currencyClassName = "",
    loading = false,
    loadingProps = {},
  }) => {
    const convertedPrice = numberConvertor(price, compactCurrencyType, digitAfterFraction, skipCurrencyUnit)

    if (attachCurrency) {
      return attachCurrencyToPrice({
        price: convertedPrice,
        isCurrency: true,
        currencyClassName,
        priceClassName,
        loading,
        loadingProps,
      })
    }

    return (
      <Text className={cx(styles.priceClass, priceClassName)} loading={loading} loadingProps={loadingProps}>
        {convertedPrice}
      </Text>
    )
  }

  const formatPrice = ({ price, digitAfterFraction = 0, loading = false, loadingProps }) => {
    const formattedPrice = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: digitAfterFraction,
      maximumFractionDigits: digitAfterFraction,
    }).format(price)

    return (
      <Text className={cx(styles.priceClass)} loading={loading} loadingProps={loadingProps}>
        {formattedPrice}
      </Text>
    )
  }

  const renderPrice = ({
    price,
    shouldShowCurrency,
    shouldCompact,
    digitAfterFraction,
    skipCurrencyUnit = false,
    loading = false,
    loadingProps = {},
  }) => {
    if (shouldCompact) {
      return getCompactedPrice({
        price,
        attachCurrency: shouldShowCurrency,
        skipCurrencyUnit,
        loading,
        loadingProps,
      })
    }

    if (shouldShowCurrency) {
      return attachCurrencyToPrice({
        price: formatPrice({ price, digitAfterFraction, loading }),
        isCurrency: true,
        loading,
        loadingProps,
      })
    }

    return formatPrice({ price, digitAfterFraction, loading })
  }

  const getPriceRange = ({
    minPrice,
    maxPrice,
    shouldRenderCurrency = true,
    shouldCompact = false,
    digitAfterFraction = 0,
    showCurrencySymbolWithMinPrice = true,
    showCurrencySymbolWithMaxPrice = false,
    separator = " – ",
    loading = false,
    loadingProps = {},
  }) => {
    if (!loading && (!minPrice || !maxPrice)) {
      return <div className={styles.noPrice}>-</div>
    }

    return (
      <Space size={6}>
        <Text>
          {renderPrice({
            price: minPrice,
            shouldShowCurrency: shouldRenderCurrency && showCurrencySymbolWithMinPrice,
            shouldCompact,
            digitAfterFraction,
            loading,
            loadingProps,
          })}
        </Text>
        {!loading && <Text className={cx(styles.priceClass)}>{separator}</Text>}
        <Text>
          {renderPrice({
            price: maxPrice,
            shouldShowCurrency: shouldRenderCurrency && showCurrencySymbolWithMaxPrice,
            shouldCompact,
            digitAfterFraction,
            loading,
            loadingProps,
          })}
        </Text>
      </Space>
    )
  }

  return { getPriceRange, getCompactedPrice, attachCurrencyToPrice, formatPrice }
}

export default usePrice
